// src/pages/Expenses.tsx

import React, { useContext, useEffect, useState, useMemo } from "react";
import { Navigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { FiscalYearContext } from "../context/FiscalYearContext";
import {
  getExpenses,
  createExpense,
  updateExpense,
  deleteExpense,
} from "../api/expenses";
import {
  Expense,
  CreateExpenseRequest,
  UpdateExpenseRequest,
} from "../types/expense";
import Sidebar from "../components/Sidebar";
import ExpenseForm from "../components/ExpenseForm";
import EditExpenseForm from "../components/EditExpenseForm";
import {
  Plus,
  Download,
  Edit3,
  Trash2,
  Search,
  ReceiptText,
  X,
} from "lucide-react";
import { generatePaginationItems } from "../utils/pagination";
import { formatFiscalYear, isDateInFiscalYear } from "../utils/fiscalYear";

export default function Expenses() {
  const { user } = useContext(AuthContext);
  const { fiscalYear } = useContext(FiscalYearContext);

  // — Data state —
  const [list, setList] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);

  // — Filter state —
  type TaxFilter = "all" | "noTax" | "tax" | "vatRefundable";
  const [taxCategoryFilter, setTaxCategoryFilter] = useState<TaxFilter>("all");
  const [taxSubcategoryFilter, setTaxSubcategoryFilter] = useState<string>("");
  const [subcategoryFilter, setSubcategoryFilter] = useState<string>("");
  const [courseNameFilter, setCourseNameFilter] = useState<string>("all");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  // — Tax subcategories for dropdown (with categoryType) —
  interface TaxSubcat {
    id: string;
    name: string;
    categoryType: string;
  }

  const [taxSubcategories, setTaxSubcategories] = useState<TaxSubcat[]>([]);

  // — Course options for dropdown —
  interface CourseOption {
    id: string;
    name: string;
  }

  const [courseOptions, setCourseOptions] = useState<CourseOption[]>([]);

  // — Bulk selection & modals —
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showNew, setShowNew] = useState(false);
  const [editTarget, setEditTarget] = useState<Expense | null>(null);

  // — Pagination state —
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // 1) Fetch expenses once
  useEffect(() => {
    let active = true;

    (async () => {
      setLoading(true);

      const exps = await getExpenses(fiscalYear);

      if (!active) return;

      setList(exps);
      setLoading(false);

      // Build unique tax‐subcategory list based on taxRate > 0
      const taxSubs: TaxSubcat[] = exps
        .filter((e) => e.taxRate != null && e.taxRate > 0 && e.subcategory)
        .map((e) => ({
          id: e.subcategory!.id,
          name: e.subcategory!.name,
          categoryType: e.subcategory!.categoryType,
        }))
        .reduce((acc: TaxSubcat[], sub) => {
          if (!acc.some((s) => s.id === sub.id)) {
            acc.push(sub);
          }
          return acc;
        }, []);

      setTaxSubcategories(taxSubs);

      // Build unique course options list
      const courses: CourseOption[] = exps
        .filter((e) => e.course?.id || e.courseName)
        .map((e) => ({
          id: e.course?.id || e.courseName || "",
          name: e.course?.name || e.courseName || "",
        }))
        .reduce((acc: CourseOption[], course) => {
          if (course.name && !acc.some((c) => c.id === course.id)) {
            acc.push(course);
          }
          return acc;
        }, [])
        .sort((a, b) => a.name.localeCompare(b.name));

      setCourseOptions(courses);
    })();

    return () => {
      active = false;
    };
  }, [fiscalYear]);

  // 2) Combined filters (including date range)
  const filtered = useMemo(() => {
    return list
      .slice()
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .filter((e) => {
        // Date range
        const expDate = new Date(e.date);

        if (startDate && expDate < new Date(startDate)) {
          return false;
        }

        if (endDate && expDate > new Date(endDate)) {
          return false;
        }

        // Tax filters
        const hasTax = e.taxRate != null && e.taxRate > 0;

        const isVatRefundable =
          e.subcategory?.categoryType === "vat-refundable";

        if (taxCategoryFilter === "tax" && !hasTax) {
          return false;
        }

        if (taxCategoryFilter === "noTax" && hasTax) {
          return false;
        }

        if (taxCategoryFilter === "vatRefundable" && !isVatRefundable) {
          return false;
        }

        if (
          (taxCategoryFilter === "tax" ||
            taxCategoryFilter === "vatRefundable") &&
          taxSubcategoryFilter &&
          e.subcategory?.id !== taxSubcategoryFilter
        ) {
          return false;
        }

        // Subcategory text search
        const subName = e.subcategory?.name.toLowerCase() ?? "";

        if (
          subcategoryFilter.trim() &&
          !subName.includes(subcategoryFilter.trim().toLowerCase())
        ) {
          return false;
        }

        // Course selection filter
        if (courseNameFilter && courseNameFilter !== "all") {
          const courseId = e.course?.id || e.courseName || "";

          if (courseId !== courseNameFilter) {
            return false;
          }
        }

        return true;
      });
  }, [
    list,
    startDate,
    endDate,
    taxCategoryFilter,
    taxSubcategoryFilter,
    subcategoryFilter,
    courseNameFilter,
  ]);

  // 3) Pagination
  const totalPages = Math.ceil(filtered.length / rowsPerPage);

  const paginatedList = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;

    return filtered.slice(start, start + rowsPerPage);
  }, [filtered, currentPage, rowsPerPage]);

  // 4) Reset page if out of range
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  // 5) Auth guard
  if (!user) {
    return <Navigate to="/" replace />;
  }

  // — Bulk select helpers —
  const allSelected =
    filtered.length > 0 && filtered.every((e) => selectedIds.includes(e.id));

  const toggleAll = () =>
    setSelectedIds(allSelected ? [] : filtered.map((e) => e.id));

  const toggleOne = (id: string) =>
    setSelectedIds((s) =>
      s.includes(id) ? s.filter((x) => x !== id) : [...s, id],
    );

  // — CRUD handlers —
  const handleCreate = async (data: CreateExpenseRequest) => {
    const e = await createExpense(data);

    if (isDateInFiscalYear(e.date, fiscalYear)) {
      setList((prev) => [e, ...prev]);
    }

    // update taxSubcategories if needed...
    setTaxCategoryFilter("all");
    setTaxSubcategoryFilter("");
    setSubcategoryFilter("");
    setCourseNameFilter("all");
    setStartDate("");
    setEndDate("");
    setCurrentPage(1);
    setShowNew(false);
  };

  const handleUpdate = async (id: string, data: UpdateExpenseRequest) => {
    const e = await updateExpense(id, data);

    setList((l) =>
      isDateInFiscalYear(e.date, fiscalYear)
        ? l.map((x) => (x.id === id ? e : x))
        : l.filter((x) => x.id !== id),
    );

    setEditTarget(null);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this expense?")) {
      return;
    }

    await deleteExpense(id);

    setList((l) => l.filter((x) => x.id !== id));

    setSelectedIds((s) => s.filter((x) => x !== id));
  };

  // — CSV export logic —
  const csvEscape = (v: string | number) => {
    const s = String(v).replace(/"/g, '""');

    return /[",\n]/.test(s) ? `"${s}"` : s;
  };

  const exportSelected = () => {
    const rows = (
      selectedIds.length ? list.filter((e) => selectedIds.includes(e.id)) : list
    ).map((e) => {
      const net = e.amount;
      const taxAmt = e.taxRate != null ? (net * e.taxRate) / 100 : 0;
      const total = net + taxAmt;

      const payTo =
        e.teacher?.name ?? e.teacherName ?? e.staff?.name ?? e.staffName ?? "–";

      return [
        new Date(e.date).toLocaleDateString(),
        e.createdBy?.username ?? e.createdByName ?? "",
        e.subcategory.name,
        e.remarks ?? "",
        e.course?.name ?? e.courseName ?? "",
        net.toFixed(2),
        e.taxRate != null ? e.taxRate : "",
        taxAmt > 0 ? taxAmt.toFixed(2) : "",
        total.toFixed(2),
        payTo,
      ];
    });

    const headers = [
      "Date",
      "CreatedBy",
      "Subcategory",
      "Remarks",
      "Course",
      "NetAmount",
      "TaxRate",
      "TaxAmount",
      "TotalAmount",
      "PayTo",
    ];

    const csv = [
      headers.map(csvEscape).join(","),
      ...rows.map((r) => r.map(csvEscape).join(",")),
    ].join("\n");

    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8",
    });

    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");

    a.href = url;
    a.download = "expenses_selected_export.csv";
    a.click();

    URL.revokeObjectURL(url);
  };

  const clearFilters = () => {
    setTaxCategoryFilter("all");
    setTaxSubcategoryFilter("");
    setSubcategoryFilter("");
    setCourseNameFilter("all");
    setStartDate("");
    setEndDate("");
  };

  const fieldClass = `
    box-border h-10 w-full min-w-0 rounded-[9px]
    border border-slate-200 bg-white px-3
    text-[12px] font-medium text-slate-800
    outline-none transition
    placeholder:text-slate-400
    hover:border-slate-300
    focus:border-slate-400
    focus:ring-2 focus:ring-slate-100
  `;

  const iconButtonClass = `
    inline-flex h-8 w-8 shrink-0 items-center justify-center
    rounded-lg border bg-white p-0 transition
    focus:outline-none focus:ring-2
  `;

  const startIndex = (currentPage - 1) * rowsPerPage;

  return (
    <div className="min-h-screen bg-[#f7f8fb]">
      <Sidebar />

      <main
        className="
          ml-[280px] min-w-0 px-6 py-6
          transition-[margin] duration-200
          [.sidebar-collapsed_&]:ml-[76px]
          max-lg:ml-[76px]
          max-lg:px-4
          max-sm:px-3
        "
      >
        <div className="mx-auto w-full max-w-[1600px]">
          {/* Header */}
          <header className="mb-5 flex items-start justify-between gap-4 max-md:flex-col max-md:items-stretch">
            <div className="flex min-w-0 items-start gap-3">
              <div
                className="
                  grid h-10 w-10 shrink-0 place-items-center
                  rounded-[10px] bg-slate-100 text-slate-600
                "
              >
                <ReceiptText size={20} strokeWidth={1.8} />
              </div>

              <div className="min-w-0">
                <div
                  className="
                    mb-1 text-[10px] font-bold uppercase
                    tracking-[0.12em] text-blue-600
                  "
                >
                  Expense management
                </div>

                <h1
                  className="
                    m-0 text-[28px] font-bold
                    tracking-[-0.03em] text-slate-950
                    max-sm:text-[24px]
                  "
                >
                  Expenses
                </h1>

                <p className="mb-0 mt-1 text-[12px] leading-5 text-slate-500">
                  Showing {formatFiscalYear(fiscalYear)}
                </p>
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-2 max-sm:grid max-sm:grid-cols-2">
              {user?.role !== "viewer" && (
                <button
                  type="button"
                  onClick={exportSelected}
                  className="
                    inline-flex h-10 items-center justify-center gap-2
                    rounded-[9px]
                    border border-emerald-200
                    bg-white px-4
                    text-[11px] font-semibold text-emerald-700
                    transition
                    hover:border-emerald-300
                    hover:bg-emerald-50
                    hover:text-emerald-800
                    focus:outline-none
                    focus:ring-2
                    focus:ring-emerald-200
                  "
                >
                  <Download size={15} />
                  Export {selectedIds.length ? "Selected" : "All"}
                </button>
              )}

              <button
                type="button"
                onClick={() => setShowNew(true)}
                className="
                  inline-flex h-10 items-center justify-center gap-2
                  rounded-[9px]
                  border border-emerald-600
                  bg-emerald-600 px-4
                  text-[11px] font-semibold text-white
                  transition
                  hover:border-emerald-700
                  hover:bg-emerald-700
                  focus:outline-none
                  focus:ring-2
                  focus:ring-emerald-200
                "
              >
                <Plus size={15} />
                New Expense
              </button>
            </div>
          </header>

          {/* Filters */}
          <section
            className="
              mb-4 rounded-[16px]
              border border-slate-200 bg-white
              p-4
              shadow-[0_1px_3px_rgba(15,23,42,0.04)]
            "
          >
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-3 xl:grid-cols-6">
              {/* Subcategory search */}
              <label className="min-w-0 xl:col-span-2">
                <span
                  className="
                    mb-1.5 block
                    text-[10px] font-semibold uppercase
                    tracking-[0.04em] text-slate-400
                  "
                >
                  Subcategory
                </span>

                <div className="relative">
                  <Search
                    size={16}
                    className="
                      pointer-events-none absolute left-3 top-1/2
                      -translate-y-1/2 text-slate-400
                    "
                  />

                  <input
                    type="text"
                    placeholder="Search by subcategory…"
                    value={subcategoryFilter}
                    onChange={(e) => setSubcategoryFilter(e.target.value)}
                    className={`${fieldClass} pl-9`}
                  />
                </div>
              </label>

              {/* Course */}
              <label className="min-w-0">
                <span
                  className="
                    mb-1.5 block
                    text-[10px] font-semibold uppercase
                    tracking-[0.04em] text-slate-400
                  "
                >
                  Course
                </span>

                <select
                  value={courseNameFilter}
                  onChange={(e) => setCourseNameFilter(e.target.value)}
                  className={fieldClass}
                >
                  <option value="all">All Courses</option>

                  {courseOptions.map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.name}
                    </option>
                  ))}
                </select>
              </label>

              {/* Tax category */}
              <label className="min-w-0">
                <span
                  className="
                    mb-1.5 block
                    text-[10px] font-semibold uppercase
                    tracking-[0.04em] text-slate-400
                  "
                >
                  Tax Type
                </span>

                <select
                  value={taxCategoryFilter}
                  onChange={(e) => {
                    const v = e.target.value as TaxFilter;

                    setTaxCategoryFilter(v);
                    setTaxSubcategoryFilter("");
                  }}
                  className={fieldClass}
                >
                  <option value="all">All Expenses</option>
                  <option value="tax">With Tax</option>
                  <option value="noTax">No Tax</option>
                  <option value="vatRefundable">VAT-Refundable</option>
                </select>
              </label>

              {/* Start */}
              <label className="min-w-0">
                <span
                  className="
                    mb-1.5 block
                    text-[10px] font-semibold uppercase
                    tracking-[0.04em] text-slate-400
                  "
                >
                  From Date
                </span>

                <input
                  type="date"
                  aria-label="Start date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className={fieldClass}
                />
              </label>

              {/* End */}
              <label className="min-w-0">
                <span
                  className="
                    mb-1.5 block
                    text-[10px] font-semibold uppercase
                    tracking-[0.04em] text-slate-400
                  "
                >
                  To Date
                </span>

                <input
                  type="date"
                  aria-label="End date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className={fieldClass}
                />
              </label>

              {/* Conditional tax subcategory */}
              {(taxCategoryFilter === "tax" ||
                taxCategoryFilter === "vatRefundable") && (
                <label className="min-w-0 xl:col-span-2">
                  <span
                    className="
                      mb-1.5 block
                      text-[10px] font-semibold uppercase
                      tracking-[0.04em] text-slate-400
                    "
                  >
                    Tax Subcategory
                  </span>

                  <select
                    value={taxSubcategoryFilter}
                    onChange={(e) => setTaxSubcategoryFilter(e.target.value)}
                    className={fieldClass}
                  >
                    <option value="">
                      {taxCategoryFilter === "tax"
                        ? "All Tax Subcategories"
                        : "All VAT-Refundable Subcategories"}
                    </option>

                    {taxSubcategories
                      .filter((sub) =>
                        taxCategoryFilter === "tax"
                          ? sub.categoryType !== "vat-refundable"
                          : sub.categoryType === "vat-refundable",
                      )
                      .map((sub) => (
                        <option key={sub.id} value={sub.id}>
                          {sub.name}
                        </option>
                      ))}
                  </select>
                </label>
              )}
            </div>

            <div
              className="
                mt-4 flex flex-wrap items-center justify-between gap-2
                border-t border-slate-100 pt-3
              "
            >
              <span className="text-[10px] text-slate-400">
                Filters apply to expense records.
              </span>

              <button
                type="button"
                onClick={clearFilters}
                className="
                  inline-flex h-8 items-center justify-center gap-1.5
                  rounded-lg
                  border border-emerald-200
                  bg-white px-3
                  text-[13px] font-semibold text-emerald-700
                  transition
                  hover:border-emerald-300
                  hover:bg-emerald-50
                  hover:text-emerald-800
                  focus:outline-none
                  focus:ring-2
                  focus:ring-emerald-200
                "
              >
                <X size={13} />
                Clear filters
              </button>
            </div>
          </section>

          {/* Loading / table */}
          {loading ? (
            <section
              className="
                flex min-h-[260px] flex-col items-center justify-center
                rounded-[16px]
                border border-slate-200 bg-white
                shadow-[0_1px_3px_rgba(15,23,42,0.04)]
              "
            >
              <span
                className="
                  h-6 w-6 animate-spin rounded-full
                  border-[3px] border-slate-200 border-t-slate-700
                "
              />

              <span className="mt-3 text-[11px] font-medium text-slate-500">
                Loading expenses…
              </span>
            </section>
          ) : (
            <section
              className="
                overflow-hidden rounded-[16px]
                border border-slate-200 bg-white
                shadow-[0_1px_3px_rgba(15,23,42,0.04)]
              "
            >
              {/* Table header */}
              <div
                className="
                  flex items-center justify-between gap-4
                  border-b border-slate-100
                  px-5 py-4
                  max-sm:flex-col max-sm:items-start max-sm:px-4
                "
              >
                <div>
                  <h2 className="m-0 text-[18px] font-bold text-slate-950">
                    Expense Records
                  </h2>

                  <p className="mb-0 mt-1 text-[13px] text-slate-500">
                    Showing{" "}
                    <strong className="font-semibold text-slate-600">
                      {filtered.length}
                    </strong>{" "}
                    of{" "}
                    <strong className="font-semibold text-slate-600">
                      {list.length}
                    </strong>{" "}
                    expense
                    {list.length === 1 ? "" : "s"}
                  </p>
                </div>

                {selectedIds.length > 0 && (
                  <span
                    className="
                      rounded-full bg-emerald-50 px-3 py-1.5
                      text-[11px] font-semibold text-emerald-700
                    "
                  >
                    {selectedIds.length} selected
                  </span>
                )}
              </div>

              {filtered.length > 0 ? (
                <>
                  <div className="w-full overflow-x-auto">
                    <table className="w-full min-w-[1550px] border-collapse">
                      <thead className="bg-slate-50/80">
                        <tr>
                          {/* <th className="w-[52px] px-4 py-3.5 text-center">
                            <input
                              type="checkbox"
                              checked={
                                allSelected
                              }
                              onChange={
                                toggleAll
                              }
                              className="
                                h-4 w-4 cursor-pointer
                                rounded border-slate-300
                                accent-emerald-600
                              "
                            />
                          </th> */}

                          {[
                            "Date / Created By",
                            "Subcategory",
                            "Remarks",
                            "Course",
                            "Amount Before Tax",
                            "Tax Rate",
                            "Tax Amount",
                            "Total Amount",
                            "Pay To",
                          ].map((label) => (
                            <th
                              key={label}
                              className="
                                  px-4 py-3.5 text-left
                                  text-[12px] font-semibold uppercase
                                  tracking-[0.04em] text-slate-600
                                "
                            >
                              {label}
                            </th>
                          ))}

                          <th
                            className="
                              w-[110px] px-4 py-3.5 text-center
                              text-[12px] font-semibold uppercase
                              tracking-[0.04em] text-slate-600
                            "
                          >
                            Actions
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {paginatedList.map((e) => {
                          const net = e.amount;

                          const taxAmt =
                            e.taxRate != null ? (net * e.taxRate) / 100 : 0;

                          const total = net + taxAmt;

                          const payTo =
                            e.teacher?.name ??
                            e.teacherName ??
                            e.staff?.name ??
                            e.staffName ??
                            "–";

                          return (
                            <tr
                              key={e.id}
                              className="
                                  border-b border-slate-100
                                  transition-colors
                                  last:border-b-0
                                  hover:bg-slate-50/60
                                "
                            >
                              {/* <td className="px-4 py-4 text-center align-middle">
                                <input
                                  type="checkbox"
                                  checked={selectedIds.includes(e.id)}
                                  onChange={() => toggleOne(e.id)}
                                  className="
                                      h-4 w-4 cursor-pointer
                                      rounded border-slate-300
                                      accent-emerald-600
                                    "
                                />
                              </td> */}

                              <td className="px-4 py-4 align-middle">
                                <div className="min-w-[150px]">
                                  <div className="text-[13px] font-semibold text-slate-800">
                                    {new Date(e.date).toLocaleDateString()}
                                  </div>

                                  <div className="mt-1 text-[11px] text-slate-500">
                                    {e.createdBy?.username ??
                                      e.createdByName ??
                                      "—"}
                                  </div>
                                </div>
                              </td>

                              <td className="px-4 py-4 align-middle">
                                <span
                                  className={`
                                      inline-flex max-w-[210px]
                                      rounded-full px-2.5 py-1
                                      text-[11px] font-semibold
                                      ${
                                        e.subcategory.categoryType ===
                                        "non-taxable"
                                          ? "bg-slate-100 text-slate-700"
                                          : "bg-amber-50 text-amber-700"
                                      }
                                    `}
                                  title={e.subcategory.name}
                                >
                                  <span className="truncate">
                                    {e.subcategory.name}
                                  </span>
                                </span>
                              </td>

                              <td className="px-4 py-4 align-middle">
                                <div
                                  className="
                                      min-w-[160px] max-w-[250px]
                                      break-words text-[13px]
                                      leading-5 text-slate-600
                                    "
                                >
                                  {e.remarks ?? "–"}
                                </div>
                              </td>

                              <td className="px-4 py-4 align-middle">
                                <div
                                  className="
                                      min-w-[150px] max-w-[220px]
                                      break-words text-[13px]
                                      font-medium text-slate-700
                                    "
                                >
                                  {e.course?.name ?? e.courseName ?? "–"}
                                </div>
                              </td>

                              <td className="px-4 py-4 align-middle">
                                <div className="min-w-[130px] text-[13px] font-medium text-slate-700">
                                  Rs {net.toFixed(2)}
                                </div>
                              </td>

                              <td className="px-4 py-4 align-middle">
                                <div className="min-w-[80px] text-[13px] text-slate-600">
                                  {e.taxRate != null ? `${e.taxRate}%` : "–"}
                                </div>
                              </td>

                              <td className="px-4 py-4 align-middle">
                                <div className="min-w-[110px] text-[13px] text-slate-600">
                                  {taxAmt > 0 ? `Rs ${taxAmt.toFixed(2)}` : "–"}
                                </div>
                              </td>

                              <td className="px-4 py-4 align-middle">
                                <div className="min-w-[120px] text-[14px] font-bold text-emerald-700">
                                  Rs {total.toFixed(2)}
                                </div>
                              </td>

                              <td className="px-4 py-4 align-middle">
                                <div
                                  className="
                                      min-w-[140px] max-w-[220px]
                                      break-words text-[13px]
                                      font-medium text-slate-700
                                    "
                                >
                                  {payTo}
                                </div>
                              </td>

                              <td className="px-4 py-4 text-center align-middle">
                                <div className="inline-flex items-center justify-center gap-1.5">
                                  <button
                                    type="button"
                                    onClick={() => setEditTarget(e)}
                                    title="Edit"
                                    className={`
                                        ${iconButtonClass}
                                        border-emerald-200
                                        text-emerald-700
                                        hover:border-emerald-300
                                        hover:bg-emerald-50
                                        hover:text-emerald-800
                                        focus:ring-emerald-200
                                      `}
                                  >
                                    <Edit3 size={15} />
                                  </button>

                                  <button
                                    type="button"
                                    onClick={() => handleDelete(e.id)}
                                    title="Delete"
                                    className={`
                                        ${iconButtonClass}
                                        border-red-200
                                        text-red-600
                                        hover:border-red-300
                                        hover:bg-red-50
                                        hover:text-red-700
                                        focus:ring-red-200
                                      `}
                                  >
                                    <Trash2 size={15} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {filtered.length > rowsPerPage && (
                    <div
                      className="
                        flex items-center justify-between gap-4
                        border-t border-slate-100
                        px-5 py-3
                        max-sm:flex-col max-sm:items-stretch max-sm:px-4
                      "
                    >
                      <div className="flex flex-wrap items-center gap-2 text-[13px] text-slate-600">
                        <span>Rows per page</span>

                        <select
                          value={rowsPerPage}
                          onChange={(e) => {
                            setRowsPerPage(Number(e.target.value));
                            setCurrentPage(1);
                          }}
                          className="
                            h-8 rounded-lg
                            border border-slate-200 bg-white
                            px-2 text-[13px] font-medium text-slate-700
                            outline-none
                            focus:border-slate-400
                            focus:ring-2
                            focus:ring-slate-100
                          "
                        >
                          {[5, 10, 20, 50].map((n) => (
                            <option key={n} value={n}>
                              {n}
                            </option>
                          ))}
                        </select>

                        <span>
                          {startIndex + 1}–
                          {Math.min(startIndex + rowsPerPage, filtered.length)}{" "}
                          of {filtered.length}
                        </span>
                      </div>

                      <div className="flex flex-wrap items-center justify-end gap-1.5 max-sm:justify-start">
                        <button
                          type="button"
                          onClick={() =>
                            setCurrentPage((p) => Math.max(p - 1, 1))
                          }
                          disabled={currentPage === 1}
                          className="
                            h-8 rounded-lg
                            border border-emerald-200
                            bg-white px-3
                            text-[13px] font-semibold text-emerald-700
                            transition
                            hover:border-emerald-300
                            hover:bg-emerald-50
                            disabled:cursor-not-allowed
                            disabled:opacity-40
                          "
                        >
                          Prev
                        </button>

                        {generatePaginationItems(currentPage, totalPages).map(
                          (item, index) =>
                            item.type === "ellipsis" ? (
                              <span
                                key={`ellipsis-${index}`}
                                className="
                                  inline-flex h-8 min-w-8
                                  items-center justify-center
                                  text-[13px] font-semibold text-slate-500
                                "
                              >
                                …
                              </span>
                            ) : (
                              <button
                                type="button"
                                key={item.page}
                                onClick={() => setCurrentPage(item.page!)}
                                className={`
                                  h-8 min-w-8 rounded-lg border px-2
                                  text-[13px] font-semibold transition
                                  ${
                                    item.isActive
                                      ? `
                                          border-emerald-600
                                          bg-emerald-600
                                          text-white
                                        `
                                      : `
                                          border-emerald-200
                                          bg-white
                                          text-emerald-700
                                          hover:border-emerald-300
                                          hover:bg-emerald-50
                                        `
                                  }
                                `}
                              >
                                {item.page}
                              </button>
                            ),
                        )}

                        <button
                          type="button"
                          onClick={() =>
                            setCurrentPage((p) => Math.min(p + 1, totalPages))
                          }
                          disabled={currentPage === totalPages}
                          className="
                            h-8 rounded-lg
                            border border-emerald-200
                            bg-white px-3
                            text-[13px] font-semibold text-emerald-700
                            transition
                            hover:border-emerald-300
                            hover:bg-emerald-50
                            disabled:cursor-not-allowed
                            disabled:opacity-40
                          "
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div
                  className="
                    flex min-h-[230px] flex-col
                    items-center justify-center px-6 text-center
                  "
                >
                  <div
                    className="
                      grid h-11 w-11 place-items-center
                      rounded-xl bg-slate-100 text-slate-400
                    "
                  >
                    <ReceiptText size={20} />
                  </div>

                  <h3 className="mb-0 mt-3 text-[13px] font-semibold text-slate-900">
                    No expenses found
                  </h3>

                  <p
                    className="
                      mb-0 mt-1 max-w-[430px]
                      text-[10px] leading-5 text-slate-500
                    "
                  >
                    {subcategoryFilter ||
                    (courseNameFilter && courseNameFilter !== "all")
                      ? "Try changing your filters to see more expense records."
                      : "No expenses are available to display."}
                  </p>
                </div>
              )}
            </section>
          )}

          {/* Modals */}
          {showNew && (
            <ExpenseForm
              onSubmit={handleCreate}
              onCancel={() => setShowNew(false)}
            />
          )}

          {!!editTarget && (
            <EditExpenseForm
              expense={editTarget}
              onSubmit={handleUpdate}
              onCancel={() => setEditTarget(null)}
            />
          )}
        </div>
      </main>
    </div>
  );
}
